import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FolderPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const KQ = {
  CQC: ["Safe", "Effective", "Caring", "Responsive", "Well-led"],
  Ofsted: ["Overall experiences and progress", "How well children are helped and protected", "Effectiveness of leaders and managers"],
};

export default function SaveToEvidence({ sourceType, refId, defaultTitle, regulator = "CQC", variant = "outline", size = "sm" }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(defaultTitle || "");
  const [kq, setKq] = useState((KQ[regulator] || KQ.CQC)[0]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/evidence", { title, key_question: kq, source_type: sourceType, ref_id: refId });
      toast.success("Saved to your evidence folder");
      setOpen(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button variant={variant} size={size} onClick={() => setOpen(true)} data-testid={`save-evidence-${sourceType}`}>
        <FolderPlus className="w-4 h-4 mr-1.5" /> Save to evidence
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="save-evidence-dialog">
          <DialogHeader><DialogTitle className="font-display">File in your evidence folder</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5" data-testid="save-evidence-title" />
            </div>
            <div>
              <Label>Key question</Label>
              <Select value={kq} onValueChange={setKq}>
                <SelectTrigger className="mt-1.5" data-testid="save-evidence-kq"><SelectValue /></SelectTrigger>
                <SelectContent>{(KQ[regulator] || KQ.CQC).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || title.trim().length < 2} data-testid="save-evidence-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
